import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ChannelInput, NowPlayingResponse, PublicChannel } from '../types';
import { api } from './client';
import { qk } from './keys';

export function useChannels() {
  return useQuery({
    queryKey: qk.channels(true),
    queryFn: () => api.get<{ items: PublicChannel[] }>('/admin/channels'),
  });
}

export function useChannel(slug: string | null) {
  return useQuery({
    queryKey: qk.channel(slug ?? ''),
    enabled: !!slug,
    queryFn: () => api.get<PublicChannel>(`/channels/${encodeURIComponent(slug ?? '')}`),
  });
}

export function useNowPlaying(slug: string | null, locale: string) {
  return useQuery({
    queryKey: qk.nowPlaying(slug ?? '', locale),
    enabled: !!slug,
    refetchInterval: 30_000,
    queryFn: () =>
      api.get<NowPlayingResponse>(`/channels/${encodeURIComponent(slug ?? '')}/now-playing`, {
        locale,
      }),
  });
}

export function useCreateChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ChannelInput) => api.post<PublicChannel>('/admin/channels', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channels'] }),
  });
}

export function useUpdateChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; input: Partial<ChannelInput> }) =>
      api.patch<PublicChannel>(`/admin/channels/${args.id}`, args.input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channels'] }),
  });
}

export function useDeleteChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/admin/channels/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channels'] }),
  });
}

export type DetectLiveResult =
  | {
      detected: true;
      videoId: string;
      title: string | null;
      thumbnailUrl: string | null;
      actualStartTime: string | null;
      concurrentViewers: number | null;
    }
  | { detected: false; reason: 'not-live' };

/**
 * Manual-click YouTube live-stream discovery — one `search.list` (100 quota
 * units) that finds the currently-airing broadcast for the channel's pinned
 * YouTube channel id / handle and auto-pins its video id. Precondition: the
 * channel row must have `youtubeChannelId` OR `youtubeHandle` set.
 */
export function useDetectChannelLiveStream() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (idOrSlug: string) =>
      api.post<DetectLiveResult>(
        `/admin/channels/${encodeURIComponent(idOrSlug)}/youtube/detect-live`,
        {},
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channels'] }),
  });
}
